'use client';
import React, { useState, useEffect } from 'react';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEnabled: () => void;
}

export default function TwoFactorSetupModal({ isOpen, onClose, onEnabled }: TwoFactorSetupModalProps) {
  const [step, setStep] = useState<'qr' | 'verify' | 'success'>('qr');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep('qr');
      setCode('');
      setError('');
      fetchSetup();
    }
  }, [isOpen]);

  const fetchSetup = async () => {
    setFetching(true);
    setError('');
    try {
      const res = await fetch('/api/2fa/setup', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Setup failed');
      setQrDataUrl(data.qrDataUrl);
      setSecret(data.secret);
    } catch (err: any) {
      setError(err.message || 'Failed to initialize 2FA setup');
    } finally {
      setFetching(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/2fa/verify-setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onEnabled();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={step !== 'success' ? onClose : undefined} />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-base font-semibold text-foreground">Set Up Two-Factor Auth</h2>
          </div>
          {step !== 'success' && (
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="px-5 py-5 space-y-4">
          {/* Step indicator */}
          {step !== 'success' && (
            <div className="flex items-center gap-2 mb-1">
              {(['qr', 'verify'] as const).map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-1.5 text-xs font-medium ${step === s ? 'text-primary' : step === 'verify' && s === 'qr' ? 'text-success' : 'text-muted-foreground'}`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === s ? 'bg-primary text-primary-foreground' : step === 'verify' && s === 'qr' ? 'bg-success text-white' : 'bg-muted text-muted-foreground'}`}>
                      {step === 'verify' && s === 'qr' ? '✓' : i + 1}
                    </div>
                    {s === 'qr' ? 'Scan QR' : 'Verify'}
                  </div>
                  {i === 0 && <div className="flex-1 h-px bg-border" />}
                </React.Fragment>
              ))}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          {step === 'qr' && (
            <>
              <p className="text-sm text-muted-foreground">
                Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.), then click <strong>Next</strong>.
              </p>
              <div className="flex flex-col items-center gap-3 py-2">
                {fetching ? (
                  <div className="w-44 h-44 rounded-xl bg-muted flex items-center justify-center">
                    <svg className="w-8 h-8 text-muted-foreground animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                ) : qrDataUrl ? (
                  <img src={qrDataUrl} alt="2FA QR code for authenticator app" className="w-44 h-44 rounded-xl border border-border" />
                ) : null}
                {secret && (
                  <div className="w-full">
                    <p className="text-xs text-muted-foreground text-center mb-1.5">Or enter this key manually:</p>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted border border-border">
                      <code className="flex-1 text-xs font-mono text-foreground tracking-widest break-all">{secret}</code>
                      <button
                        onClick={() => navigator.clipboard?.writeText(secret)}
                        className="flex-shrink-0 p-1 rounded hover:bg-border transition-colors text-muted-foreground hover:text-foreground"
                        title="Copy secret"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => { setStep('verify'); setError(''); }}
                disabled={fetching || !qrDataUrl}
                className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                Next — Enter Code
              </button>
            </>
          )}

          {step === 'verify' && (
            <>
              <p className="text-sm text-muted-foreground">
                Open your authenticator app and enter the <strong>6-digit code</strong> shown for MuyuRide.
              </p>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Authentication Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-xl text-foreground text-center tracking-[0.5em] font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setStep('qr'); setCode(''); setError(''); }}
                  className="flex-1 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors text-foreground"
                >
                  Back
                </button>
                <button
                  onClick={handleVerify}
                  disabled={loading || code.length !== 6}
                  className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Verifying…</>
                  ) : 'Enable 2FA'}
                </button>
              </div>
            </>
          )}

          {step === 'success' && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">2FA Enabled!</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Your account is now protected with two-factor authentication. You'll be asked for a code on every login.
                </p>
              </div>
              <button
                onClick={handleDone}
                className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
